/**
 * Modals for the Teams module: create (with an initial-roster picker in the
 * same flow, per the "create + add members in one action" decision this
 * module was built to), add members to an existing team, and delete.
 */
import { useEffect, useMemo, useState } from 'react';
import { Modal, Field, SearchInput } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';

/** A searchable checkbox list of users — shared by the create form and "add members". */
function UserPicker({ users, selected, onToggle, excludeIds = [] }) {
  const [q, setQ] = useState('');

  const candidates = useMemo(() => {
    const excluded = new Set(excludeIds);
    const term = q.trim().toLowerCase();
    return users
      .filter((u) => !excluded.has(u.value))
      .filter((u) => !term || u.label.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term));
  }, [users, excludeIds, q]);

  return (
    <div>
      <SearchInput value={q} onChange={setQ} placeholder="Search people…" className="mb-2" />
      <div className="border rounded" style={{ maxHeight: 220, overflowY: 'auto' }}>
        {candidates.length === 0 ? (
          <div className="p-3 t-sm text-muted">No one matches.</div>
        ) : candidates.map((u) => (
          <label key={u.value} className="d-flex align-items-center gap-2 px-3 py-2 border-bottom t-sm" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selected.includes(u.value)}
              onChange={() => onToggle(u.value)}
            />
            {u.label}
          </label>
        ))}
      </div>
      {selected.length > 0 && <div className="t-xs text-muted mt-1">{selected.length} selected</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Create                                                                      */
/* -------------------------------------------------------------------------- */

const BLANK = { code: '', name: '', manager_id: '' };

export function CreateTeamModal({ open, onClose, onSubmit, busy, userOptions }) {
  const [form, setForm] = useState(BLANK);
  const [memberIds, setMemberIds] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) { setForm(BLANK); setMemberIds([]); setErrors({}); }
  }, [open]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const toggleMember = (id) => setMemberIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  function validate() {
    const next = {};
    if (!form.code.trim()) next.code = 'A short code is required.';
    if (!form.name.trim()) next.name = 'A name is required.';
    if (!form.manager_id) next.manager_id = 'Choose the team manager.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a team"
      footer={
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-add btn-sm-x" disabled={busy}
            onClick={() => {
              if (!validate()) return;
              onSubmit({ code: form.code.trim(), name: form.name.trim(), manager_id: form.manager_id }, memberIds);
            }}
          >
            {busy ? <><ButtonSpinner />Creating…</> : 'Create team'}
          </button>
        </>
      }
    >
      <p className="t-sm ink-2 mb-3">
        Pick the manager who will own this team. Only that manager (and admins)
        can see it.
      </p>
      <div className="row g-3">
        <div className="col-6">
          <Field label="Code" required error={errors.code} help="Short and unique, e.g. NORDICS.">
            <input className="form-control" value={form.code} onChange={set('code')} placeholder="NORDICS" autoFocus />
          </Field>
        </div>
        <div className="col-6">
          <Field label="Name" required error={errors.name}>
            <input className="form-control" value={form.name} onChange={set('name')} placeholder="Nordics Sales Team" />
          </Field>
        </div>
      </div>
      <Field label="Team manager" required error={errors.manager_id} className="mt-2">
        <select className="form-select" value={form.manager_id} onChange={set('manager_id')}>
          <option value="">Select a manager…</option>
          {userOptions.users.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
        </select>
      </Field>
      <Field label="Add members now (optional)" className="mt-2" help="You can add or remove people later from the team's page.">
        <UserPicker users={userOptions.users} selected={memberIds} onToggle={toggleMember} />
      </Field>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Add members                                                                 */
/* -------------------------------------------------------------------------- */

export function AddMembersModal({ open, onClose, onSubmit, busy, userOptions, existingMemberIds = [] }) {
  const [memberIds, setMemberIds] = useState([]);

  useEffect(() => { if (open) setMemberIds([]); }, [open]);

  const toggleMember = (id) => setMemberIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add members"
      footer={
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-add btn-sm-x" disabled={busy || !memberIds.length}
            onClick={() => onSubmit(memberIds)}
          >
            {busy ? <><ButtonSpinner />Adding…</> : `Add ${memberIds.length || ''} member${memberIds.length === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <UserPicker users={userOptions.users} selected={memberIds} onToggle={toggleMember} excludeIds={existingMemberIds} />
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Delete                                                                      */
/* -------------------------------------------------------------------------- */

export function DeleteTeamModal({ open, team, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');

  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete team"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-delete btn-sm-x"
            disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}
          >
            {busy ? <><ButtonSpinner />Deleting…</> : 'Delete'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Deleting <strong>{team?.name}</strong>. Refuses while sub-teams nest
        under it or members still belong to it — clear those first.
      </p>
      <Field label="Reason" required help="Written to the audit trail.">
        <input
          className="form-control" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Reorganised into two regional teams" autoFocus
        />
      </Field>
    </Modal>
  );
}
